import React, { useState, FC, type CSSProperties } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import { XStack, YStack, useThemeName } from 'tamagui'
import { Text } from '@newComponents/Text'

import { Grid } from '@newComponents/Grid'
import { Separator } from '@newComponents/Separator'
import { Link, ExternalLink } from '@newComponents/Link'

import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import WindowNew from '@newComponents/Icon/icons/WindowNew'

import { getPurpleBoxShadow } from './UnauthenticatedHomeScreen/styles'

export const HomeScreenFeaturesCard: FC = () => {
  const isDark = useThemeName() === 'dark'

  return (
    <YStack
      padding='$6'
      backgroundColor='$backgroundSoft'
      borderRadius='$8'
      style={{ boxShadow: getPurpleBoxShadow(isDark) } as CSSProperties}
    >
      <YStack gap='$10'>
        <YStack gap='$8' $gtXs={{ display: 'none' }}>
          <GuildsAndNetworks />

          <Events />

          <Presentations />
        </YStack>
        <YStack display='none' $gtXs={{ display: 'flex' }}>
          <Grid gap='$6'>
            <Grid.Column>
              <GuildsAndNetworks />
            </Grid.Column>

            <Grid.Column>
              <Events />
            </Grid.Column>

            <Grid.Column>
              <Presentations />
            </Grid.Column>
          </Grid>
        </YStack>

        <YStack alignItems='center'>
          <Separator borderBottomColor='$borderColor' width={60} />
        </YStack>

        <YStack alignItems='center'>
          <Text fontSize='$4' color='$colorMuted'>
            <Trans>Not sure about a feature?</Trans>
          </Text>

          <Text fontSize='$4' color='$colorMuted' textAlign='center'>
            <Trans>
              <ExternalLink href='mailto:hi@guild.host'>
                <Text fontSize='$4' color='$colorMuted'>
                  Get in touch
                </Text>
              </ExternalLink>
              , we&apos;d love to chat with you!
            </Trans>
          </Text>
        </YStack>
      </YStack>
    </YStack>
  )
}

const GuildsAndNetworks: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#A78BFA' : '#5533EB'
  const circleBg = isDark
    ? 'rgba(167, 139, 250, 0.2)'
    : 'rgba(85, 51, 235, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Guilds & Networks</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          { href: '/docs/guilds/overview', label: t`Create a Following` },
          { href: '/docs/guilds/members', label: t`Manage your Members` },
          { href: '/docs/guilds/members', label: t`3-tier Permissions System` },
          {
            href: '/docs/guilds/forms',
            label: t`Membership Forms & Approvals`,
          },
          {
            href: '/docs/guilds/overview',
            label: t`Inter-connect with Networks`,
          },
          {
            href: '/docs/guilds/overview',
            label: t`Cross-promote via Networks`,
          },
          { href: '/docs/guilds/monetization', label: t`Accept Donations` },
          {
            href: '/docs/guilds/monetization',
            label: t`Sponsorships & Analytics`,
          },
          { href: '/docs/guilds/messaging', label: t`Message your Members` },
          { href: '/docs/guilds/settings', label: t`Notify your Slack` },
          { href: '/docs/guilds/profile', label: t`Themed Guild Pages` },
          { href: '/docs/guilds/profile', label: t`Beautiful Social Sharing` },
          { href: '/docs/guilds/embeds', label: t`Embeddable Widgets` },
          { href: '/docs/guilds/bluesky', label: t`Announce on Bluesky` },
          {
            href: '/docs/guilds/meetup',
            label: t`Mirror your Meetup.com events`,
          },
          {
            href: '/docs/guilds/google-calendar',
            label: t`Personal Google Calendar Sync`,
          },
          { href: '/api/next/docs', label: t`API Access`, external: true },
        ].map((bullet) => (
          <XStack gap='$2' key={`${bullet.href}:${bullet.label}`}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            <FeatureLinkBulletItem
              href={bullet.href}
              label={bullet.label}
              external={bullet.external}
            />
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const FeatureLinkBulletItem = ({
  href,
  label,
  external = false,
}: {
  href: string
  label: string
  external?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const WrappingComponent = external ? ExternalLink : Link

  return (
    <WrappingComponent
      href={href}
      flex={1}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      size='$4'
      animation='medium'
      color='$color'
      hoverStyle={{ color: '$primaryColor', textDecorationLine: 'none' }}
    >
      <XStack gap='$2'>
        <Text flex={1} size='$4' color='inherit'>
          {label}
        </Text>

        <XStack
          animation='medium'
          x={isHovered ? 4 : 0}
          width='$4'
          height='$4'
          flexShrink={0}
        >
          <XStack opacity={0.4} position='absolute'>
            <IconWrapper size='$4' color='$color'>
              <WindowNew />
            </IconWrapper>
          </XStack>

          <XStack
            animation='medium'
            opacity={isHovered ? 0.8 : 0}
            position='absolute'
          >
            <IconWrapper size='$4' color='$primaryColor'>
              <WindowNew />
            </IconWrapper>
          </XStack>
        </XStack>
      </XStack>
    </WrappingComponent>
  )
}

const Events: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#F5A0F6' : '#F26AF4'
  const circleBg = isDark
    ? 'rgba(242, 106, 244, 0.2)'
    : 'rgba(242, 106, 244, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Events</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          { href: '/docs/events/overview', label: t`In-Person Events` },
          { href: '/docs/events/overview', label: t`Online Events` },
          {
            href: '/docs/events/overview',
            label: t`Hybrid In-Person & Online Events`,
          },
          { href: '/docs/events/multi-host', label: t`Multi-Host Events` },
          { href: '/docs/events/tiers', label: t`Multi-Tier Ticketing` },
          { href: '/docs/events/discounts', label: t`Discount Codes` },
          {
            href: '/docs/events/orders',
            label: t`Paid Ticket Order Records`,
          },
          { href: '/docs/events/rsvps', label: t`Optional Waitlist` },
          {
            href: '/docs/events/attendee-forms',
            label: t`Attendance Forms & Approvals`,
          },
          { href: '/docs/events/messages', label: t`Message your Attendees` },
          { href: '/docs/events/overview', label: t`Connect Presentations` },
          { href: '/docs/events/overview', label: t`Calendar Sync & Export` },
          { href: '/docs/events/page', label: t`Themed Event Pages` },
          { href: '/docs/events/overview', label: t`Beautiful Social Sharing` },
          { href: '/docs/events/embeds', label: t`Embeddable Widgets` },
          { href: '/api/next/docs', label: t`API Access`, external: true },
        ].map((bullet) => (
          <XStack gap='$2' key={`${bullet.href}:${bullet.label}`}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            <FeatureLinkBulletItem
              href={bullet.href}
              label={bullet.label}
              external={bullet.external}
            />
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const Presentations: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#FBAD7E' : '#F9894B'
  const circleBg = isDark
    ? 'rgba(249, 137, 75, 0.2)'
    : 'rgba(249, 137, 75, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Presentations</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          {
            href: '/docs/presentations/overview',
            label: t`Manage your Guild's Content Library`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Manage your Personal Content Library`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Connect Presentations to Events`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Connect Presentations to Guilds`,
          },
          {
            href: '/docs/presentations/claiming',
            label: t`Claim Presentations`,
          },
          {
            href: '/docs/presentations/creation',
            label: t`Connect Speakers & Bios`,
          },
          {
            href: '/docs/presentations/page',
            label: t`Video Links (YouTube, Vimeo)`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Curate Content from top Presenters`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Share Content across Networks`,
          },
          {
            href: '/docs/presentations/page',
            label: t`Themed Presentation Pages`,
          },
          {
            href: '/docs/presentations/overview',
            label: t`Beautiful Social Sharing`,
          },
          {
            href: '/docs/presentations/embeds',
            label: t`Embeddable Widgets`,
          },
          { href: '/api/next/docs', label: t`API Access`, external: true },
        ].map((bullet) => (
          <XStack gap='$2' key={`${bullet.href}:${bullet.label}`}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            <FeatureLinkBulletItem
              href={bullet.href}
              label={bullet.label}
              external={bullet.external}
            />
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}
